import { baseApi } from './baseApi';

export const workspaceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // List my workspaces
    listWorkspaces: builder.query({
      query: () => '/workspaces',
      providesTags: ['Workspace'],
    }),

    // Resolved entitlements with provenance — what the workspace actually
    // gets, and where each number came from. Distinct from billing, which
    // reports what was purchased. The gap between the two is the point.
    getEntitlements: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/entitlements`,
      transformResponse: (r) => r?.data ?? r,
      providesTags: (_r, _e, id) => [{ type: 'Workspace', id }, 'Subscription'],
    }),

    // Get workspace details
    getWorkspace: builder.query({
      query: (id) => `/workspaces/${id}`,
      // Also a 'Members' provider: this query returns workspace.members, which
      // the HiveLink member/DM pickers read. Adding a teammate (employeeApi
      // invalidates 'Members') must refetch it, or the new person never appears
      // in "add members" until a manual page reload.
      providesTags: (_result, _err, id) => [{ type: 'Workspace', id }, 'Members'],
    }),

    // Create workspace
    createWorkspace: builder.mutation({
      query: (body) => ({ url: '/workspaces', method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),

    // Update workspace
    updateWorkspace: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/workspaces/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'Workspace', id }, 'Workspace'],
    }),

    // Delete workspace
    deleteWorkspace: builder.mutation({
      query: (id) => ({ url: `/workspaces/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Workspace'],
    }),

    // Switch active workspace
    switchWorkspace: builder.mutation({
      query: (id) => ({ url: `/workspaces/${id}/switch`, method: 'POST' }),
      invalidatesTags: ['Workspace', 'User'],
    }),

    // Get members
    getMembers: builder.query({
      query: ({ workspaceId, ...params }) => ({
        url: `/workspaces/${workspaceId}/members`,
        params,
      }),
      providesTags: ['Workspace'],
    }),

    // Invite member
    inviteMember: builder.mutation({
      query: ({ workspaceId, ...body }) => ({
        url: `/workspaces/${workspaceId}/members/invite`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Invite bulk
    inviteBulk: builder.mutation({
      query: ({ workspaceId, invites }) => ({
        url: `/workspaces/${workspaceId}/members/invite-bulk`,
        method: 'POST',
        body: { invites },
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Accept invite (public)
    acceptInvite: builder.mutation({
      query: (body) => ({ url: '/workspaces/invites/accept', method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),

    // Get pending invites
    getPendingInvites: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/invites`,
      providesTags: ['Workspace'],
    }),

    // Cancel invite
    cancelInvite: builder.mutation({
      query: ({ workspaceId, inviteId }) => ({
        url: `/workspaces/${workspaceId}/invites/${inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Update member role
    updateWorkspaceMemberRole: builder.mutation({
      query: ({ workspaceId, memberId, role }) => ({
        url: `/workspaces/${workspaceId}/members/${memberId}/role`,
        method: 'PUT',
        body: { role },
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Remove member
    removeWorkspaceMember: builder.mutation({
      query: ({ workspaceId, memberId }) => ({
        url: `/workspaces/${workspaceId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Leave workspace
    leaveWorkspace: builder.mutation({
      query: (workspaceId) => ({
        url: `/workspaces/${workspaceId}/leave`,
        method: 'POST',
      }),
      invalidatesTags: ['Workspace'],
    }),

    // Get industry templates
    getTemplates: builder.query({
      query: () => '/workspaces/templates',
      providesTags: ['Templates'],
    }),

    // Vertical catalogue — roles/workflows/invoice defaults per industry
    getVerticalCatalogue: builder.query({
      query: () => '/workspaces/vertical-catalogue',
      providesTags: ['Templates'],
    }),

    // Get full template detail (for preview)
    getFullTemplate: builder.query({
      query: (industry) => `/workspaces/templates/${industry}`,
    }),

    // Create project from template
    createProjectFromWorkspaceTemplate: builder.mutation({
      query: ({ workspaceId, ...body }) => ({
        url: `/workspaces/${workspaceId}/project-from-template`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Project'],
    }),

    // Apply template
    applyTemplate: builder.mutation({
      query: ({ workspaceId, ...body }) => ({
        url: `/workspaces/${workspaceId}/apply-template`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, { workspaceId }) => [{ type: 'Workspace', id: workspaceId }],
    }),

    // Update statuses
    updateStatuses: builder.mutation({
      query: ({ workspaceId, statuses }) => ({
        url: `/workspaces/${workspaceId}/statuses`,
        method: 'PUT',
        body: { statuses },
      }),
      invalidatesTags: (_result, _err, { workspaceId }) => [{ type: 'Workspace', id: workspaceId }],
    }),

    // ═══ ADMIN: Billing, Security, Export, AI ═══
    getBilling: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/billing`,
      providesTags: ['Billing'],
    }),
    getAvailablePlans: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/available-plans`,
      providesTags: ['Plans'],
    }),
    subscribeToPlan: builder.mutation({
      query: ({ workspaceId, ...body }) => ({ url: `/workspaces/${workspaceId}/subscribe`, method: 'POST', body }),
      invalidatesTags: ['Billing', 'Plans', 'Workspace'],
    }),
    cancelWorkspaceSubscription: builder.mutation({
      query: ({ workspaceId, reason }) => ({ url: `/workspaces/${workspaceId}/cancel-subscription`, method: 'POST', body: { reason } }),
      invalidatesTags: ['Billing', 'Plans', 'Workspace'],
    }),
    updateBilling: builder.mutation({
      query: ({ workspaceId, ...body }) => ({ url: `/workspaces/${workspaceId}/billing`, method: 'PUT', body }),
      invalidatesTags: ['Billing', 'Workspace'],
    }),
    getSecuritySettings: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/security`,
      providesTags: ['Security'],
    }),
    updateSecuritySettings: builder.mutation({
      query: ({ workspaceId, ...body }) => ({ url: `/workspaces/${workspaceId}/security`, method: 'PUT', body }),
      invalidatesTags: ['Security'],
    }),
    getCustomFields: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/custom-fields`,
      providesTags: ['CustomFields'],
    }),
    updateCustomFields: builder.mutation({
      query: ({ workspaceId, fields }) => ({ url: `/workspaces/${workspaceId}/custom-fields`, method: 'PUT', body: { fields } }),
      invalidatesTags: ['CustomFields'],
    }),
    exportWorkspaceData: builder.query({
      query: ({ workspaceId, format }) => ({
        url: `/workspaces/${workspaceId}/export?format=${format || 'json'}`,
        // For CSV the server sends text/csv — return raw Response so caller can blob it
        responseHandler: async (response) => {
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('text/csv') || ct.includes('text/plain')) {
            const text = await response.text();
            return { __csv: true, text };
          }
          return response.json();
        },
      }),
    }),
    getAdminInsights: builder.query({
      query: (workspaceId) => `/workspaces/${workspaceId}/admin-insights`,
      providesTags: ['AdminInsights'],
    }),
  }),
});

export const {
  useListWorkspacesQuery,
  useGetWorkspaceQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useSwitchWorkspaceMutation,
  useGetMembersQuery,
  useInviteMemberMutation,
  useInviteBulkMutation,
  useAcceptInviteMutation,
  useGetPendingInvitesQuery,
  useCancelInviteMutation,
  useUpdateWorkspaceMemberRoleMutation,
  useRemoveWorkspaceMemberMutation,
  useLeaveWorkspaceMutation,
  useGetTemplatesQuery,
  useGetVerticalCatalogueQuery,
  useGetFullTemplateQuery,
  useApplyTemplateMutation,
  useCreateProjectFromWorkspaceTemplateMutation,
  useUpdateStatusesMutation,
  useGetBillingQuery, useUpdateBillingMutation, useGetAvailablePlansQuery,
  useSubscribeToPlanMutation, useCancelWorkspaceSubscriptionMutation,
  useGetSecuritySettingsQuery, useUpdateSecuritySettingsMutation,
  useGetCustomFieldsQuery, useUpdateCustomFieldsMutation,
  useExportWorkspaceDataQuery, useGetAdminInsightsQuery,
  useGetEntitlementsQuery,
} = workspaceApi;